import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { numOrNull } from '../utils/buildPoolEngineInput';
import { describeShareEquivalent } from '../utils/poolShare';
import {
  POOL_STRATEGY_OPTIONS, changesShareBase, readsShare, restateShare, shareLabelFor,
} from '../utils/poolShareStrategy';

// O-2783 "pool over offers". Per-member share control for the active offer:
// a pool_payout_strategy select + a pool_percentage input. Both bind to the
// active member offer and persist through the EXISTING offer autosave
// (onChangeField → updateLocalOffer → debounced offer PATCH).
//
// Rendered only in Pool mode, on the active member's deal-terms pane.

const OR = 'OR';
const AND = 'AND';

const V3PoolMemberShare = ({ offer, earnings, onChangeField }) => {
  const strategy = offer?.pool_payout_strategy || 'explicit';
  // The member's own guarantee, drawn FROM the pool — distinct from the offer's
  // guarantee that helps PRODUCE the pool (which is configured on the pool deal).
  // Hidden by V3OfferDealTermsForm in pool mode, which left "the opener takes a
  // flat $250, then the rest splits 60/40" — the shape this feature exists for —
  // impossible to express in this editor at all.
  const operator = offer?.dollar_or_percentage_operator || AND;
  const showPercentage = readsShare(strategy);
  const percentageLabel = shareLabelFor(strategy);
  const shareEquivalent = describeShareEquivalent(strategy, offer?.pool_percentage, earnings);
  const shareEquivalentId = `pool-member-share-equivalent-${offer?.id}`;

  // The raw keystrokes, so the field can be momentarily empty without persisting
  // an empty. Clearing the seeded share to type a real one is the normal gesture,
  // and a blank PATCH is rejected for this strategy — which would roll the
  // STRATEGY back too, undoing the selection the booker just made.
  const [shareDraft, setShareDraft] = useState(null);

  // The keystroke draft must not outlive the record it was typed against —
  // switching artist would otherwise show the previous member's digits over the
  // new one's stored share.
  useEffect(() => { setShareDraft(null); }, [offer?.id]);

  const onChangeShare = (raw) => {
    setShareDraft(raw);
    const next = numOrNull(raw);
    if (next === null && strategy === 'percentage_of_remainder') return;
    onChangeField('pool_percentage', next);
  };

  const onChangeStrategy = (next) => {
    if (next === strategy) return;

    onChangeField('pool_payout_strategy', next);
    setShareDraft(null);

    if (!changesShareBase(strategy, next)) return;
    onChangeField('pool_percentage', restateShare({
      offerId: offer?.id, from: strategy, to: next, share: offer?.pool_percentage ?? null,
    }));
  };

  return (
    <section className="deal-terms-card-v2 pool-member-share" data-testid="v3-pool-member-share">
      <div className="deal-terms-card-v2__title-block" style={{ padding: '12px 16px 0' }}>
        <span className="deal-terms-card-v2__title">Pool share</span>
        <span className="deal-terms-card-v2__subtitle">
          How this artist draws from the pool.
        </span>
      </div>

      <div className="deal-terms-grid" style={{ padding: '12px 16px' }}>
        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Payout strategy</span>
          <select
            className="deal-terms-field__select"
            data-testid="pool-member-strategy"
            value={strategy}
            onChange={(e) => onChangeStrategy(e.target.value)}
          >
            {POOL_STRATEGY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Guarantee</span>
          <span className="deal-terms-field__input-wrap">
            <span className="deal-terms-field__prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-field__input"
              data-testid="pool-member-guarantee"
              value={offer?.dollar_amount ?? ''}
              onChange={(e) => onChangeField('dollar_amount', numOrNull(e.target.value))}
            />
          </span>
        </label>

        {showPercentage && (
          <div className="deal-terms-field deal-terms-field--operator">
            <span className="deal-terms-field__label">Combine</span>
            <div className="deal-terms-form__operator-group" role="radiogroup" aria-label="Guarantee combiner">
              <button
                type="button"
                className={`deal-terms-form__operator-btn ${operator === AND ? 'deal-terms-form__operator-btn--active' : ''}`}
                data-testid="pool-member-operator-and"
                onClick={() => onChangeField('dollar_or_percentage_operator', AND)}
                aria-pressed={operator === AND}
                aria-label="Guarantee plus share"
              >
                +
              </button>
              <button
                type="button"
                className={`deal-terms-form__operator-btn ${operator === OR ? 'deal-terms-form__operator-btn--active' : ''}`}
                data-testid="pool-member-operator-or"
                onClick={() => onChangeField('dollar_or_percentage_operator', OR)}
                aria-pressed={operator === OR}
                aria-label="Guarantee versus share"
              >
                vs
              </button>
            </div>
          </div>
        )}

        {showPercentage && (
          <label className="deal-terms-field">
            <span className="deal-terms-field__label">{percentageLabel}</span>
            <span className="deal-terms-field__input-wrap">
              <input
                type="number"
                step="0.01"
                className="deal-terms-field__input"
                data-testid="pool-member-percentage"
                aria-label={percentageLabel}
                aria-describedby={shareEquivalent ? shareEquivalentId : undefined}
                value={shareDraft !== null ? shareDraft : (offer?.pool_percentage ?? '')}
                onChange={(e) => onChangeShare(e.target.value)}
                onBlur={() => setShareDraft(null)}
              />
              <span className="deal-terms-field__suffix">%</span>
            </span>
            {shareEquivalent && (
              <span id={shareEquivalentId} className="pool-member-share__equivalent" data-testid="pool-member-share-equivalent">
                {shareEquivalent}
              </span>
            )}
          </label>
        )}
        {/* O-4755. Cap/floor clamp this member's take whatever the strategy —
          * the engine applies them to a remainder line the same as an explicit
          * one — so unlike the share they show for every strategy. Present in
          * the simple editor's pool card since PR A; here too, so a booker sees
          * the same member deal in the same place in either editor. */}
        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Payout cap</span>
          <span className="deal-terms-field__input-wrap">
            <span className="deal-terms-field__prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-field__input"
              data-testid="pool-member-cap"
              value={offer?.artist_payout_cap ?? ''}
              onChange={(e) => onChangeField('artist_payout_cap', numOrNull(e.target.value))}
            />
          </span>
        </label>

        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Payout floor</span>
          <span className="deal-terms-field__input-wrap">
            <span className="deal-terms-field__prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-field__input"
              data-testid="pool-member-floor"
              value={offer?.artist_payout_floor ?? ''}
              onChange={(e) => onChangeField('artist_payout_floor', numOrNull(e.target.value))}
            />
          </span>
        </label>
      </div>
    </section>
  );
};

V3PoolMemberShare.propTypes = {
  offer: PropTypes.object,
  earnings: PropTypes.object,
  onChangeField: PropTypes.func.isRequired,
};

V3PoolMemberShare.defaultProps = {
  offer: null,
  earnings: null,
};

export default V3PoolMemberShare;
